import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, Fab, IconButton, InputAdornment, Skeleton, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch,
  TextField, Tooltip, Typography, alpha, useMediaQuery, useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import Pagination from "../../../Components/Pagination";
import { IAssessmentFocusArea } from "../../../Api/Interface/api.interface";
import {
  useGetAssessmentFocusAreasQuery,
  useDeleteAssessmentFocusAreaMutation,
  useUpdateAssessmentFocusAreaMutation,
  useReorderAssessmentFocusAreasMutation,
} from "../../../Api/attoDeskApi";
import FocusAreaModal from "../Create";

// ── Step Up palette (matches other admin surfaces) ─────────────────
const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";
const PAGE_BG = "#f8fafc";

// Starter list shown ONLY when the master table is empty. Purely informational
// suggestions — clicking one opens the standard Add modal with the name
// prefilled through the modal's existing `data` prop (id-less object → the
// modal treats it as create, not edit). Nothing is inserted into the DB until
// the admin explicitly saves.
const SUGGESTED_FOCUS_AREAS: string[] = [
  "Communication",
  "Social Skills",
  "Behavior Regulation",
  "Academic Skills",
  "Daily Living",
  "Emotional Regulation",
  "Executive Functioning",
  "Fine Motor Skills",
  "Gross Motor Skills",
  "Sensory Processing",
  "Language Development",
  "Cognitive Skills",
];

// Deterministic pastel-avatar colour for a focus area name (same pattern used
// on the student rows) — just visual differentiation, no semantic meaning.
const PALETTE: [string, string][] = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#166534"], ["#fef3c7", "#92400e"],
  ["#f3e8ff", "#6b21a8"], ["#ffe4e6", "#9f1239"], ["#e0f2fe", "#075985"],
];
function avatarFor(name: string) {
  const trimmed = (name || "?").trim();
  const initials =
    trimmed.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  const [bg, color] = PALETTE[hash % PALETTE.length];
  return { initials, bg, color };
}

// ── Small stat cell for the summary strip ─────────────────────────
const StatCell: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode; accent?: string }> = ({
  label, value, icon, accent,
}) => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.75, py: 1.25, flex: "1 1 160px", minWidth: 140 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: "10px",
        bgcolor: alpha(accent || NAVY, 0.1),
        color: accent || NAVY,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK, lineHeight: 1.1 }}>{value ?? 0}</Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    </Box>
  </Stack>
);

const Index = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IAssessmentFocusArea | null>(null);
  const [toDelete, setToDelete] = useState<IAssessmentFocusArea | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Two queries so the "no data at all" empty state can be distinguished from
  // the "no results for this search" empty state without an extra network hop
  // on every keystroke — the unfiltered query only runs while a search is
  // active. If backend caching returns fresh data the first request is reused.
  const { data, isLoading, isError, isFetching } = useGetAssessmentFocusAreasQuery({ search: debouncedSearch });
  const { data: unfilteredData } = useGetAssessmentFocusAreasQuery(
    { search: "" },
    { skip: !debouncedSearch }
  );
  const [deleteArea] = useDeleteAssessmentFocusAreaMutation();
  const [updateArea] = useUpdateAssessmentFocusAreaMutation();
  const [reorderAreas] = useReorderAssessmentFocusAreasMutation();

  const rows: IAssessmentFocusArea[] = Array.isArray(data?.data)
    ? (data?.data as unknown as IAssessmentFocusArea[])
    : [];
  const totalUnfiltered: number = debouncedSearch
    ? (Array.isArray(unfilteredData?.data) ? (unfilteredData!.data as unknown as IAssessmentFocusArea[]).length : rows.length)
    : rows.length;
  const isTrulyEmpty = !debouncedSearch && rows.length === 0;
  const isSearchNoMatch = !!debouncedSearch && rows.length === 0;

  // ── Drag-and-drop ordering (disabled while searching) ──────────
  const [orderedRows, setOrderedRows] = useState<IAssessmentFocusArea[]>([]);
  const orderedRef = useRef<IAssessmentFocusArea[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const dragEnabled = !debouncedSearch;

  useEffect(() => {
    setOrderedRows(rows);
    orderedRef.current = rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDragStart = (absIndex: number) => {
    dragIndexRef.current = absIndex;
  };

  const handleDragOver = (e: React.DragEvent, absIndex: number) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === absIndex) return;
    setOrderedRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(absIndex, 0, moved);
      orderedRef.current = next;
      return next;
    });
    dragIndexRef.current = absIndex;
  };

  const handleDrop = async () => {
    dragIndexRef.current = null;
    try {
      const items = orderedRef.current.map((r, i) => ({ id: r.id, sort_order: i + 1 }));
      const res: any = await reorderAreas({ items }).unwrap();
      if (res.status) {
        showMessage("Focus area order updated successfully.");
      } else {
        showErrorMessage(res.message || "Failed to update order");
      }
    } catch {
      showErrorMessage("Failed to update focus area order");
    }
  };

  const handleToggleActive = async (row: IAssessmentFocusArea) => {
    try {
      const res: any = await updateArea({ id: row.id, body: { is_active: !row.is_active } }).unwrap();
      if (!res.status) showErrorMessage(res.message || "Failed to update status");
    } catch {
      showErrorMessage("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res: any = await deleteArea(toDelete.id).unwrap();
      if (res.status) {
        showMessage(res.message || "Deleted successfully");
        setDeleteOpen(false);
        setToDelete(null);
      } else {
        showErrorMessage(res.message || "Failed to delete");
      }
    } catch {
      showErrorMessage("Failed to delete focus area");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (row: IAssessmentFocusArea) => {
    setEditing(row);
    setModalOpen(true);
  };
  // Open the Add modal with only the name prefilled. The modal's `data` prop
  // usually carries a full row for edit; passing an id-less synthetic object
  // keeps the modal in "create" mode (its `isEdit` check is `!!data?.id`) but
  // seeds the Name field, so the admin can review, adjust, and save. Nothing
  // is written until they hit Save — this matches the spec's requirement that
  // suggestions must NOT auto-insert.
  const openCreateFromSuggestion = (name: string) => {
    setEditing({ name } as unknown as IAssessmentFocusArea);
    setModalOpen(true);
  };

  // Counts derived client-side from the currently loaded (search-filtered) set.
  const counts = useMemo(() => {
    const active = rows.filter((r) => r.is_active).length;
    return { total: rows.length, active, inactive: rows.length - active };
  }, [rows]);

  const pageRows = orderedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: PAGE_BG, minHeight: "100%" }}>
      {/* ═══ Header card ═══════════════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, borderColor: HAIRLINE }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                bgcolor: alpha(NAVY, 0.09), color: NAVY,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CenterFocusStrongOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
                Assessment Focus Areas
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
                Create and manage the master list of focus areas used across assessments.
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.4, opacity: 0.85 }}>
                Reorder items to control how they appear in forms and reports.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ sm: "center" }}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <TextField
              size="small"
              placeholder="Search focus areas..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff" },
              }}
              sx={{ width: { xs: "100%", sm: 260 } }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 13,
                borderRadius: 2, boxShadow: "none",
                bgcolor: NAVY, "&:hover": { bgcolor: "#152d55", boxShadow: "none" },
                display: { xs: "none", sm: "inline-flex" },
                px: 2,
              }}
            >
              Add Focus Area
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* ═══ Summary strip ═════════════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 3, borderColor: HAIRLINE, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex", flexWrap: "wrap",
            "& > *": {
              borderRight: { md: `1px solid ${HAIRLINE}` },
              borderBottom: { xs: `1px solid ${HAIRLINE}`, md: "none" },
            },
            "& > *:last-of-type": { borderRight: "none", borderBottom: "none" },
          }}
        >
          <StatCell label="Total"    value={counts.total}    icon={<ChecklistOutlinedIcon fontSize="small" />}    accent={NAVY} />
          <StatCell label="Active"   value={counts.active}   icon={<CheckCircleOutlineIcon fontSize="small" />}   accent="#16a34a" />
          <StatCell label="Inactive" value={counts.inactive} icon={<PauseCircleOutlineIcon fontSize="small" />}   accent={MUTED} />
          <StatCell
            label="Order"
            value={<Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{dragEnabled ? "Custom" : "Search"}</Box>}
            icon={<SwapVertOutlinedIcon fontSize="small" />}
            accent="#f59e0b"
          />
        </Box>
      </Card>

      {/* ═══ Info / help card ═════════════════════════════════════════════ */}
      <Card
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, borderColor: HAIRLINE,
          bgcolor: alpha(NAVY, 0.02),
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 3 }}>
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: "10px", flexShrink: 0,
                bgcolor: alpha(NAVY, 0.1), color: NAVY,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <LightbulbOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
                What are focus areas?
              </Typography>
              <Typography sx={{ fontSize: 13, color: INK, lineHeight: 1.55 }}>
                Focus areas organise assessment items into meaningful categories such as communication,
                academics, behaviour, social skills, daily living, and other developmental or learning domains.
                They give staff a shared vocabulary when building assessments and reading reports.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
              Tips
            </Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2.5, "& li": { fontSize: 12.5, color: INK, mb: 0.5, lineHeight: 1.5 } }}>
              <li>Keep names short and clear (one to three words works best).</li>
              <li>Avoid duplicates — search first, then add.</li>
              <li>Use <b>Inactive</b> to retire an item without losing history.</li>
              <li>Drag rows to control the order forms and reports use.</li>
            </Stack>
          </Box>
        </Stack>
      </Card>

      {/* ═══ Main content card ═══════════════════════════════════════════ */}
      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: HAIRLINE, overflow: "hidden" }}>
        {/* Reorder hint — only when reordering is currently possible */}
        {dragEnabled && orderedRows.length > 1 && (
          <Box
            sx={{
              px: 2, py: 1,
              bgcolor: "#fafbfc",
              borderBottom: `1px solid ${HAIRLINE}`,
              display: "flex", alignItems: "center", gap: 1,
            }}
          >
            <SwapVertOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              Drag rows to change the display order in forms and reports.
            </Typography>
          </Box>
        )}

        {isError ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Box sx={{ fontWeight: 700 }}>Unable to load focus areas</Box>
              Please refresh the page and try again.
            </Alert>
          </Box>
        ) : isLoading || (isFetching && rows.length === 0) ? (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} />
              ))}
            </Stack>
          </Box>
        ) : isSearchNoMatch ? (
          // ── Search-specific empty state ────────────────────────────
          <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
            <Box
              sx={{
                width: 56, height: 56, borderRadius: "50%",
                bgcolor: alpha(NAVY, 0.08), color: NAVY,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                mb: 1.5,
              }}
            >
              <SearchIcon />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
              No focus areas match your search
            </Typography>
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>
              Try a different keyword or clear the search.
              {totalUnfiltered > 0 && ` ${totalUnfiltered} focus area${totalUnfiltered === 1 ? "" : "s"} in total.`}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setSearch(""); setPage(0); }}
              sx={{ mt: 2, textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Clear search
            </Button>
          </Box>
        ) : isTrulyEmpty ? (
          // ── First-time empty state + suggested focus areas ────────
          <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 4, md: 5 } }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: "50%",
                  bgcolor: alpha(NAVY, 0.08), color: NAVY,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  mb: 1.5,
                }}
              >
                <CenterFocusStrongOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK }}>
                No assessment focus areas yet
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75, mx: "auto", maxWidth: 460 }}>
                Create your first focus area to start organising assessment categories.
                Common examples include Communication, Social Skills, Academic Skills, and Daily Living.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                sx={{
                  mt: 2, textTransform: "none", borderRadius: 2, fontWeight: 700,
                  bgcolor: NAVY, boxShadow: "none",
                  "&:hover": { bgcolor: "#152d55", boxShadow: "none" },
                  px: 2.5,
                }}
              >
                Add Focus Area
              </Button>
            </Box>

            {/* Suggestions grid — informational; click to prefill the modal */}
            <Box
              sx={{
                borderRadius: 2,
                border: `1px dashed ${HAIRLINE}`,
                bgcolor: "#fff",
                p: { xs: 2, md: 2.5 },
                maxWidth: 760, mx: "auto",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: INK, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Suggested focus areas
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: MUTED, mb: 1.5 }}>
                Not sure where to start? Click any suggestion to open the Add form with the name filled in — nothing is saved until you hit Save.
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {SUGGESTED_FOCUS_AREAS.map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    icon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => openCreateFromSuggestion(name)}
                    clickable
                    sx={{
                      height: 28,
                      fontSize: 12.5,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      color: NAVY,
                      border: `1px solid ${HAIRLINE}`,
                      "& .MuiChip-icon": { color: NAVY },
                      "&:hover": {
                        bgcolor: alpha(NAVY, 0.06),
                        borderColor: alpha(NAVY, 0.4),
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        ) : (
          // ── Standard table ────────────────────────────────────────
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: "#fafbfc", borderBottom: `1px solid ${HAIRLINE}`, fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", py: 1.25 } }}>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell>Name</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell sx={{ width: 150 }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row, index) => {
                  const absIndex = page * rowsPerPage + index;
                  const av = avatarFor(row.name || "?");
                  const notesText = row.notes || "";
                  return (
                    <TableRow
                      key={row.id}
                      draggable={dragEnabled}
                      onDragStart={() => handleDragStart(absIndex)}
                      onDragOver={(e) => dragEnabled && handleDragOver(e, absIndex)}
                      onDrop={() => dragEnabled && handleDrop()}
                      sx={{
                        transition: "background-color 0.15s",
                        "&:hover": { bgcolor: "#fafbfc" },
                        "& td": { borderBottom: `1px solid ${HAIRLINE}`, py: 1.25 },
                        "&:last-of-type td": { borderBottom: "none" },
                      }}
                    >
                      <TableCell sx={{ px: 1, width: 44 }}>
                        <Tooltip title={dragEnabled ? "Drag to reorder" : "Clear the search to reorder"}>
                          <DragIndicatorIcon
                            sx={{
                              fontSize: 18,
                              color: dragEnabled ? MUTED : alpha(MUTED, 0.4),
                              cursor: dragEnabled ? "grab" : "not-allowed",
                              verticalAlign: "middle",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 30, height: 30, borderRadius: "50%",
                              bgcolor: av.bg, color: av.color,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 800,
                              flexShrink: 0,
                            }}
                            aria-hidden="true"
                          >
                            {av.initials}
                          </Box>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, lineHeight: 1.3 }}>
                            {row.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 380 }}>
                        {notesText ? (
                          <Tooltip
                            title={notesText}
                            placement="top-start"
                            arrow
                            enterDelay={500}
                            disableHoverListener={notesText.length < 60}
                          >
                            <Box
                              sx={{
                                fontSize: 12.5, color: MUTED,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}
                            >
                              {notesText}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box sx={{ color: "#cbd5e1", fontSize: 12.5 }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Switch
                            size="small"
                            color="success"
                            checked={row.is_active}
                            onChange={() => handleToggleActive(row)}
                          />
                          <Chip
                            label={row.is_active ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              height: 22, fontSize: 11, fontWeight: 700,
                              bgcolor: alpha(row.is_active ? "#22c55e" : MUTED, 0.12),
                              color: row.is_active ? "#16a34a" : MUTED,
                              border: `1px solid ${alpha(row.is_active ? "#22c55e" : MUTED, 0.22)}`,
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: NAVY }}>
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => { setToDelete(row); setDeleteOpen(true); }}
                            sx={{ color: "#dc2626" }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {orderedRows.length > rowsPerPage && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 2, borderTop: `1px solid ${HAIRLINE}` }}>
            <Pagination
              count={orderedRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
            />
          </Box>
        )}
      </Card>

      {/* Sticky Add button on mobile */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={openCreate}
          sx={{ position: "fixed", bottom: 24, right: 24, boxShadow: "0 6px 20px rgba(0,0,0,0.2)" }}
        >
          <AddIcon />
        </Fab>
      )}

      <FocusAreaModal open={modalOpen} onClose={() => setModalOpen(false)} data={editing} />

      {toDelete && (
        <DeletePopup
          open={deleteOpen}
          handleCloseDelete={() => { setDeleteOpen(false); setToDelete(null); }}
          onConfirm={handleDelete}
          title="Delete Focus Area"
          content={`Are you sure you want to delete "${toDelete.name}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
